import { toast } from '@/shared/lib/toast';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  payloadString,
  payloadNumber,
  payloadObject,
} from '../realtime-sync.payload';
import type { DataChangedEvent } from '../realtime-sync.types';
import type { User } from '@/types/user';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { profileService } from '@/shared/api/services/profile.service';

export function handleUserUpdated(event: DataChangedEvent): void {
  const payload = event.payload;

  wsLogger.info('[RealtimeSync] User updated:', {
    reason: payload?.reason,
    hasProfile: !!payload?.profile,
    hasTokens: payload?.tokens !== undefined,
  });

  // Update token balance — single-channel write into the Query cache (the
  // canonical owner of server data per STATE_POLICY). Subscribers re-render
  // synchronously, same UX as the old direct-store mutation.
  const tokens = payloadNumber(payload, 'tokens');
  const reason = payloadString(payload, 'reason') ?? '';
  const balanceChanged =
    tokens !== undefined ||
    reason.includes('subscription') ||
    reason.includes('token') ||
    reason.includes('trial') ||
    reason.includes('plan');

  if (tokens !== undefined) {
    queryClient.setQueryData(cacheKeys.balance, tokens);
  } else if (balanceChanged) {
    // Reached only when `tokens` was absent, so `balanceChanged` is true purely
    // because the reason says the balance moved.
    // Server signalled a balance-relevant event but omitted `tokens` in the
    // payload (partial broadcast / skip_tokens grant / serialization gap).
    // Keeping the stale value risks a render preflight reading 0 right after
    // an admin grant. Invalidate so the next consumer pulls /me.
    queryClient.invalidateQueries({ queryKey: cacheKeys.balance });
  }

  // The storefront's `availability` is computed FROM the balance the event just
  // moved: `high_balance` (>= fraud.max_balance_for_purchase) and `daily_limit`
  // (24h spend cap) are per-user verdicts baked into the catalog response. The
  // balance write above does not touch those cached catalogs, so without this
  // the paywall keeps refusing a purchase the server would now allow — seen in
  // production 2026-09-09, where a balance emptied from ~6400 to 46 still
  // showed "spend some tokens first". Every catalog that carries availability
  // is swept, not just the token packs: the same verdict rides the pack and
  // pass cards.
  if (balanceChanged) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.tokenPackages.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.storeCatalog.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.passes.all });
  }

  // Update user profile in the Query cache. Merge incoming fields into the
  // existing User instead of rebuilding from scratch — the WS payload only
  // carries what changed, so a rebuild nulls every field the message
  // doesn't mention (e.g. `has_completed_renders`, `locale`, `name`),
  // breaking the onboarding gate and other server-driven flags.
  // Same class of bug as Android `feedback_android_user_entity_round_trip`.
  const profile = payloadObject(payload, 'profile');
  if (profile?.user) {
    const currentUser = queryClient.getQueryData<User>(cacheKeys.user) ?? null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const userData = profile.user as Record<string, any>;

    if (currentUser && currentUser.id === userData.id) {
      // Whitelist of mutable User fields. Anything else in the payload is
      // ignored — the WS contract is "deltas the server pushed", not
      // "complete user replacement", and adding a field here is a deliberate
      // act so an unexpected server-side rename can't silently overwrite
      // client state.
      const MUTABLE_USER_FIELDS = [
        'email',
        'name',
        'locale',
        'email_verified_at',
        'created_at',
        'updated_at',
        'tokens_balance',
        'is_watermark_enabled',
        'is_whitelabel_enabled',
        'is_email_verified',
        'has_completed_renders',
        'should_request_feedback',
        'deletion_status',
        'account_attention',
      ] as const;

      const incomingFields: Partial<User> = {};
      for (const key of MUTABLE_USER_FIELDS) {
        if (userData[key] !== undefined) {
          // email_verified_at is special-cased to coerce empty string to
          // null so the auth-store invariant (`null | string`) holds.
          (incomingFields as Record<string, unknown>)[key] =
            key === 'email_verified_at'
              ? userData[key] || null
              : userData[key];
        }
      }

      const updatedUser: User = { ...currentUser, ...incomingFields };
      const hasChanged =
        JSON.stringify(currentUser) !== JSON.stringify(updatedUser);

      if (hasChanged) {
        queryClient.setQueryData<User>(cacheKeys.user, updatedUser);
        AuthTokenService.setUser(updatedUser);
      }
    }
  }

  // Refresh the active-subscription bundle whenever the profile carries
  // anything that contributes to `canGenerate` / `isGuestMode` / the
  // effective plan — that includes welcome-window users who have no
  // Subscription row at all but still need `isGuestMode=false` so the
  // Generate gate opens.
  if (profile && (profile.subscription || profile.plan || profile.user)) {
    const transformed = profileService.transformSubscriptionData(profile);
    queryClient.setQueryData(cacheKeys.subscriptions.active, transformed);
  }

  // Show subscription notification toast (API sends translated strings)
  const notificationMessage = payloadString(payload, 'notification_message');
  if (reason.includes('subscription')) {
    if (
      reason.includes('renewed') ||
      reason.includes('activated') ||
      reason.includes('recovered') ||
      reason.includes('restarted')
    ) {
      // API-driven messages rule (cross-app): the server owns user-facing
      // copy, frontends display as-is. If the API omits notification_message
      // for this event, skip the toast — fabricating an English fallback
      // here ships untranslated copy to all 24 locales.
      if (notificationMessage) {
        toast.success(notificationMessage);
      }
    } else if (reason.includes('billing_failed') && notificationMessage) {
      // Grace period entered — Apple/Google have started retrying payment.
      // The user must update their payment method or the subscription will
      // expire when the grace window closes. Warning variant, not error,
      // because access is still granted during the window.
      toast.warning(notificationMessage, {
        duration: 10000,
      });
    }
  }

  // Invalidate relevant queries
  queryClient.invalidateQueries({ queryKey: cacheKeys.profile });
  queryClient.invalidateQueries({ queryKey: cacheKeys.user });

  // Invalidate all subscription queries so the plans page refreshes
  if (reason.includes('subscription')) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.all });
    // Cross-device/tab backstop: a subscription change flips storefront pack
    // ownership (`has_active_pack` / `is_additional`) and Pass `is_owned` on
    // OTHER tabs. The local purchase paths invalidate these, but the WS event
    // is the only reconcile for tabs that didn't initiate the buy — without it
    // they keep the stale catalog for up to 5min.
    queryClient.invalidateQueries({ queryKey: cacheKeys.storeCatalog.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.passes.all });
  }

  // Invalidate payment history so purchase history page shows new transactions
  if (reason.includes('subscription') || reason.includes('token_purchase')) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.payments });
  }

  // Pack/Pro Plan mutations (pack_purchase / pack_revoked / pro_plan grant)
  // flip a building's server-computed `pack` / entitlement state, which lives on
  // the PROJECT resource — not the flat user payload. The tab/device that
  // initiated the mutation already invalidated projects locally (pack purchase);
  // this is the cross-device reconcile point so the per-building pack tags (Packs
  // screen) and pack-driven gates refresh on OTHER tabs/devices without a manual
  // reload. Pro Plan and the lifetime Passes are global unlimited entitlements,
  // so their grants must refetch every building's gates too — and the server
  // broadcasts BOTH under `pass_*` reasons (`pass_purchase` / `pass_renewed` /
  // `pass_revoked`; Pro Plan rides the same event names, see
  // AppleStoreController / ProPlanSubscriptionService). Matching only
  // `pro_plan` never fired, which left the nav crown stuck on the token number
  // until a manual reload. Mirrors Android's projects refetch on the same
  // reasons. `all` covers both the buildings list and per-project detail
  // queries.
  if (reason.includes('pack') || reason.includes('pro_plan') || reason.includes('pass')) {
    queryClient.invalidateQueries({ queryKey: cacheKeys.projects.all });
    // The render START gate is per-building and Kit-driven — refetch it so a
    // just-purchased Kit lifts the Generate paywall on the open building without
    // a reload. See useBuildingRenderBlockReason.
    queryClient.invalidateQueries({ queryKey: cacheKeys.queueStatus.all });
    // Cross-device/tab backstop for the storefront catalog: pack/pro_plan
    // grants flip `has_active_pack` / `is_additional` (and Pass `is_owned` for
    // a Pro Plan global unlock) on OTHER tabs that didn't initiate the buy.
    queryClient.invalidateQueries({ queryKey: cacheKeys.storeCatalog.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.passes.all });
  }
}
